/**
 * TicketCatalogPage - 수강권 관리(카탈로그) 페이지 (instructor-web)
 *
 * admin-web의 '수강권 관리'(AdminTicketCatalogPage)에 대응한다.
 * - 카탈로그 조회: 모든 강사 가능(getTicketCatalogList, Firestore read 권한)
 * - "신규 수강권 추가": owner/admin → CreateTicketCatalogModal, instructor → 문의 팝업
 * - 카탈로그 "수정": 강사 가능(updateInstructorTicketCatalog)
 * - 카탈로그 "삭제": owner/admin → 삭제 확인, instructor → 문의 팝업
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Card } from "@vlab-corporation/design-system";
import { AppLayout } from "@/components/AppLayout";
import { QueryStateHandler } from "@/components/QueryStateHandler";
import { CreateTicketCatalogModal } from "@/components/CreateTicketCatalogModal";
import { EditTicketCatalogModal } from "@/components/EditTicketCatalogModal";
import { useToast } from "@/components/ToastProvider";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useDeleteTicketCatalog } from "@/hooks/useTicketCatalog";
import { getTicketCatalogList, type TicketCatalogItem } from "@/services/firestore/ticketCatalog";
import type { AuthUser } from "@/types/auth";

export function TicketCatalogPage() {
  const user = useAuthenticatedUser();
  if (!user) return null;
  return <TicketCatalogPageContent user={user} />;
}

/** 카탈로그 추가·삭제 권한 = 백엔드 requireAdmin과 동일 기준(users/{uid}.role). */
function isCenterManager(role: string | undefined): boolean {
  return role === "admin" || role === "owner";
}

function formatValidDays(item: TicketCatalogItem): string {
  return item.validDays != null ? `${item.validDays}일` : "무제한";
}

function TicketCatalogPageContent({ user }: { user: AuthUser }) {
  const toast = useToast();
  const { centerOwnerUid, centerId, centerName } = user.activeMembership;
  const canManage = isCenterManager(user.role);

  const [search, setSearch] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TicketCatalogItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TicketCatalogItem | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["ticketCatalog", centerOwnerUid, centerId],
    queryFn: () => getTicketCatalogList(centerOwnerUid, centerId),
    staleTime: 5 * 60 * 1000,
  });

  const deleteTicketCatalog = useDeleteTicketCatalog();

  const filtered = useMemo(() => {
    const items = data ?? [];
    const q = search.trim().toLowerCase();
    return q ? items.filter((t) => t.title.toLowerCase().includes(q)) : items;
  }, [data, search]);

  const handleAddClick = () => {
    if (canManage) setIsCreateOpen(true);
    else setIsContactOpen(true);
  };

  const handleDeleteClick = (item: TicketCatalogItem) => {
    if (canManage) setDeleteTarget(item);
    else setIsContactOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteTicketCatalog.mutate(
      { centerId, ticketId: deleteTarget.id },
      {
        onSuccess: () => {
          setDeleteTarget(null);
          void refetch();
          toast.show("수강권이 삭제되었습니다.", "success");
        },
        onError: () => {
          toast.show("수강권 삭제에 실패했습니다.", "error");
        },
      },
    );
  };

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">수강권 관리</h2>
        <Button variant="primary" size="sm" onClick={handleAddClick}>
          신규 수강권 추가
        </Button>
      </div>

      {/* 검색 */}
      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="수강권명 검색..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary-400"
        />
      </div>

      <QueryStateHandler
        isLoading={isLoading}
        error={error}
        errorMessage="수강권 목록을 불러오지 못했습니다."
        onRetry={() => refetch()}
        isEmpty={filtered.length === 0}
        emptyMessage={search ? `"${search}" 검색 결과가 없습니다.` : "등록된 수강권이 없습니다."}
      >
        <div className="space-y-1.5">
          {filtered.map((item) => (
            <Card key={item.id} className="px-4 py-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-800 truncate">{item.title}</span>
                <span className="flex-shrink-0 text-xs text-gray-400">
                  {item.type === "P" ? "P (1:1)" : "G (그룹)"}
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-500">
                총 {item.totalCoupon}회 · 사용기한 {formatValidDays(item)}
                {item.maxTrainee != null && ` · 최대 ${item.maxTrainee}인`}
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditTarget(item)}>
                  수정
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(item)}>
                  삭제
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </QueryStateHandler>

      {/* 신규 수강권 추가 모달 (owner/admin만 도달) */}
      <CreateTicketCatalogModal
        isOpen={isCreateOpen}
        centerName={centerName}
        onClose={() => setIsCreateOpen(false)}
        onCreated={() => {
          setIsCreateOpen(false);
          void refetch();
          toast.show("수강권이 등록되었습니다.", "success");
        }}
      />

      {/* 수강권 수정 모달 (강사 가능) */}
      {editTarget && (
        <EditTicketCatalogModal
          isOpen
          centerId={centerId}
          ticket={editTarget}
          onClose={() => setEditTarget(null)}
          onUpdated={() => {
            setEditTarget(null);
            void refetch();
            toast.show("수강권이 수정되었습니다.", "success");
          }}
        />
      )}

      {/* 삭제 확인 다이얼로그 (owner/admin만 도달) */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <Card className="w-full max-w-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-2">수강권 삭제</h3>
            <p className="text-sm text-gray-600 mb-5">
              "{deleteTarget.title}" 수강권을 삭제하시겠습니까?
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteTicketCatalog.isPending}
              >
                취소
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmDelete}
                disabled={deleteTicketCatalog.isPending}
              >
                {deleteTicketCatalog.isPending ? "삭제 중..." : "삭제"}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* 권한 안내 팝업 (instructor) */}
      {isContactOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <Card className="w-full max-w-sm p-6 text-center">
            <h3 className="text-base font-semibold text-gray-900 mb-2">권한 안내</h3>
            <p className="text-sm text-gray-600 mb-5">
              수강권 추가·삭제는 원장·관리자 권한이 필요합니다.
              <br />
              원장에게 문의해주세요.
            </p>
            <Button variant="primary" onClick={() => setIsContactOpen(false)} className="w-full">
              확인
            </Button>
          </Card>
        </div>
      )}
    </AppLayout>
  );
}
